import {
  AutocompleteParams,
  AutocompleteResult,
  CastorError,
  CoreInfo,
  CoreInfoLang,
  CoreInfoLangParams,
  CoreInfoParams,
  Field,
  FormSearchParams,
  FormSolrSearchParams,
  GeoRegisterGroup,
  GeoRegisterGroupParams,
  GeoRegisterGroupWithStat,
  GeoRegisterParams,
  GetRecord,
  GetRecordParams,
  GetRecordReportParams,
  HeatmapInts2D,
  HeatmapParams,
  HeatmapPng,
  Ping,
  ProjectInfo,
  ProjectInfoLang,
  ProjectInfoLangParams,
  ProjectList,
  QuickSearchParams,
  RangeFacetParams,
  RangeFacetResponse,
  RecordItem,
  RegisterFieldsParams,
  RegisterGroup,
  RegisterGroupParams,
  RegisterGroupWithStat,
  RegisterParams,
  RegisterTreeParams,
  RegisterTreeResponse,
  Report,
  ResponseObject,
  Search,
  SearchFieldsParams,
  SolrSearchParams,
  SortFieldsParams,
  VisibleFieldsParams
} from '@aipb/gulliver/castor/model';
import { isNil, isObject } from '@aipb/shared/util/other';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import cloneDeep from 'lodash-es/cloneDeep';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  AUTOCOMPLETE_MAPPER,
  FORM_SEARCH_MAPPER,
  FORM_SOLR_SEARCH_MAPPER,
  GEO_REGISTER_GROUP_MAPPER,
  GEO_REGISTER_MAPPER,
  GET_RECORD_MAPPER,
  GET_RECORD_REPORT_MAPPER,
  QUICK_SEARCH_MAPPER,
  RANGE_FACET_MAPPER,
  REGISTER_GROUP_MAPPER,
  REGISTER_MAPPER,
  REGISTER_TREE_MAPPER,
  SOLR_SEARCH_MAPPER
} from './mappers';

export type CastorRequest =
  | 'info/ping'
  | 'info/projectList'
  | 'info/projectListLang'
  | 'info/projectInfo'
  | 'info/projectInfoLang'
  | 'info/coreInfo'
  | 'info/coreInfoLang'
  | 'info/visibleFields'
  | 'info/searchFields'
  | 'info/sortFields'
  | 'info/detailReport'
  | 'info/detailReportId'
  | 'info/detailReportInfo'
  | 'info/detailReportLang'
  | 'info/detailReportCore'
  | 'info/registerFields'
  | 'info/appSpecFieldList'
  | 'info/appSpecFieldMap'
  | 'info/appSpecTypeField'
  | 'info/mmResources'
  | 'info/mmResourcesLang'
  | 'search/getRecordReport'
  | 'search/formSearch'
  | 'search/formSolrSearch'
  | 'search/solrSearch'
  | 'search/quickSearch'
  | 'search/smartSearch'
  | 'search/getRecord'
  | 'search/autocomplete'
  | 'search/register'
  | 'search/geoRegister'
  | 'search/registerGroup'
  | 'search/geoRegisterGroup'
  | 'search/heatmap'
  | 'search/registerTree'
  | 'search/rangeFacet';

@Injectable({
  providedIn: 'root'
})
export class CastorService {
  private baseUrl: string;

  constructor(private http: HttpClient) {}

  public setCastorUrl(castorUrl: string): void {
    this.baseUrl = castorUrl;
  }

  /**
   * Request slouží k tomu, aby bylo možno snadno zjistit, zda je určitý Castor7 server a projekt, který tento server obsluhuje, dostupný. V odezvě vrací identifikátor projektu, druh Castor serveru, jeho verzi a číslo buildu.
   */
  public ping(): Observable<Ping> {
    return this.get<Ping>('info/ping');
  }

  public projectList(): Observable<ProjectList> {
    return this.get<ProjectList>('info/projectList');
  }

  /**
   * Request vrací základní informace o projektu (aplikaci), jako jsou identifikátor a názvy projektu, seznam podporovaných jazyků uživatelského prostředí aplikace, defaultní jazyk ui, seznam všech cores použitých v aplikaci. Všechny názvy jsou předávány ve všech dostupných jazycích uživatelského rozhraní. Dále předává prefix pro názvy souborů klientské aplikace.
   */
  public projectInfo(): Observable<ProjectInfo> {
    return this.get<ProjectInfo>('info/projectInfo');
  }

  /**
   * Request vrací obdobné informace jako předchozí s tím rozdílem, že názvy pro uživatelské prostředí jsou předány pouze ve zvoleném jazyce uživatelského prostředí. Pokud jazyk není uveden, předají se názvy v defaultním jazyce prostředí. Pokud je požadován jazyk, pro který určitý název neexistuje, předá se název v „univerzálním“ jazyce (kód jazyka = “uni”). Pokud název neexistuje ani v tomto jazyce, použije se první nalezená jazyková verze názvu.
   *
   * @param params
   */
  public projectInfoLang(params: ProjectInfoLangParams = {}): Observable<ProjectInfoLang> {
    return this.get<ProjectInfoLang>('info/projectInfoLang', params);
  }

  /**
   * Request předá veškeré informace o jednom požadovovaném core použitém v projektu. Názvy core, serveru a jednotlivých polí jsou předávány ve všech dostupných jazycích uživatelského rozhraní.
   *
   * @param params
   */
  public coreInfo(params: CoreInfoParams = {}): Observable<CoreInfo> {
    return this.get<CoreInfo>('info/coreInfo', params);
  }

  /**
   * Request předá veškeré informace o jednom požadovovaném core použitém v projektu. Request vrací obdobné informace jako "coreInfo" s tím rozdílem, že názvy pro uživatelské prostředí jsou předány pouze ve zvoleném jazyce uživatelského prostředí. Pokud požadovaný jazyk není na seznamu jazyků uživatelského prostředí projektu, předají se názvy v defaultním jazyce prostředí. Pokud je požadován jazyk, pro který určitý název neexistuje, předá se název v „univerzálním“ jazyce (kód jazyka = “uni”). Pokud název neexistuje ani v tomto jazyce, použije se první nalezená jazyková verze názvu.
   *
   * @param params
   */
  public coreInfoLang(params: CoreInfoLangParams = {}): Observable<CoreInfoLang> {
    return this.get<CoreInfoLang>('info/coreInfoLang', params);
  }

  /**
   * Request předá seznam názvů polí, která jsou určena pro tvorbu odezvy na výsledek vyhledávání, v požadovaném jazyce prostředí. Spolu s identifikačním a „uživatelským“ názvem pole je předáván také Castor typ pole. To umožní např. různé zobrazení polí typu fráze, text nebo číslo. Je-li požadováno, názvy polí jsou seřazeny podle abecedy.
   *
   * @param params
   */
  public visibleFields(params: VisibleFieldsParams = {}): Observable<Field[]> {
    return this.get<Field[]>('info/visibleFields', params);
  }

  /**
   * Request předá seznam názvů polí, která jsou určena pro tvorbu dotazu do SOLR uživatelem, v požadovaném jazyce prostředí. Spolu s identifikačním a „uživatelským“ názvem pole je předáván také Castor typ pole. To umožní aplikaci upozornit uživatele na různé možnosti tvorby dotazu (např. u polí fráze a text). Je-li požadováno, názvy polí jsou seřazeny podle abecedy.
   *
   * @param params
   */
  public searchFields(params: SearchFieldsParams = {}): Observable<Field[]> {
    return this.get<Field[]>('info/searchFields', params);
  }

  /**
   * Request předá seznam názvů polí, která jsou určena pro řazení vyhledaných záznamů, které tvoří odezvu na requesty pro vyhledávání v SOLR. Názvy polí jsou předány v požadovaném jazyce prostředí. Je-li to požadováno, názvy polí jsou seřazeny podle abecedy. Spolu s identifikačním a „uživatelským“ názvem pole je předáván také Castor typ pole.
   *
   * @param params
   */
  public sortFields(params: SortFieldsParams = {}): Observable<Field[]> {
    return this.get<Field[]>('info/sortFields', params);
  }

  /**
   * Request provede dotaz v požadovaném core (daném coreId) umístěném na požadovaném SOLR serveru (daném serverId ). Vlastní dotaz je pokládán ve formátu SOLR query. Syntaxe dotazu pro SOLR „Standard Query Parser“ je podrobně popsána v dokumentaci SOLR. Dotaz je dále doplněn sadou parametrů, které definují, zda se má vyhledávat bez diakritiky, s použitím nevýznamných slov, synonym nebo stematizace. Jako odezvu request vrátí požadovanou stránku seznamu vyhledaných záznamů podle zadaného počátku v množině vyhledaných záznamů a o zadané velikosti stránky (počtu předaných záznamů). Defaultně jsou v záznamu předávána všechna dostupná pole. To ovšem může mít za následek zbytečně velkou odezvu na dotaz, proto je potřeba pečlivě zvážit množinu skutečně potřebných polí pro vytvoření požadované odezvy. Naopak pole obsahující identifikátor záznamu je předáváno vždy. Záznamy v odezvě mohou být setříděny podle polí, která jsou k tomu v definici projektu určena, a to vzestupně nebo sestupně, v případě abecedně řazených polí také v požadovaném jazyce (pokud je podporován). Pole určená pro řazení záznamů nesmějí být vícenásobná (multivalued=true). Pokud má být seznam řazen podle relevance vyhledaných záznamů, identifikátor pole pro řazení bude score (viz dokumentace SOLR). Pokud je to požadováno (parametry requestu obsahují platný identifikátor pole pro facety "facet.field"), předá se také seznam facetů (ve formě rejstříku nebo našeptávače) pro požadované pole odpovídajících položenému dotazu. Je-li v parametrech requestu uveden „hl“ = true, bude také předán seznam záznamů se zvýrazněnými fragmenty odpovídajícími dotazu. Zvýrazňování se primárně provede podle nastavení v konfiguraci projektu, dále pak podle vložených parametrů.
   *
   * @param params
   */
  solrSearch<T>(params: SolrSearchParams): Observable<Search<T>> {
    return this.get<Search<T>>('search/solrSearch', params);
  }

  /**
   * Request provede vyhledání v požadovaném core na požadovaném SOLR serveru. Dotaz pro vyhledávání je sestaven z jednotlivých řádek formuláře dotazu. Parametr „query“ s údaji vyhledávacího formuláře je očekáván ve formátu JSON jako pole řádek dotazu, z nichž je každá ve tvaru: {identifikátor pole, obsah editační řádky pole, logický vztah k předchozím řádkám. Z těchto hodnot je následně sestaven dotaz pro vyhledání ve formátu SOLR query. Dotaz je dále doplněn sadou parametrů, které definují, zda se má vyhledávat bez diakritiky, s použitím nevýznamných slov, synonym nebo stematizace. Pokud je to požadováno, request umožňuje v dotazu použít automatické hvězdičky zprava i zleva. V řádce dotazu lze dále použít vyhledávání slov na vzdálenost (kromě polí typu PHRASE), vyhledávání podle rozsahu nebo tzv. fuzzy logic vyhledávání (vše v souladu se syntaxí SOLR, jak je popsáno v dokumentaci k Standard Query Parser). Jako odezvu request vrátí požadovanou stránku seznamu vyhledaných záznamů podle zadaného počátku v množině vyhledaných záznamů a o zadané velikosti stránky (počtu předaných záznamů). Defaultně jsou v záznamu předávána všechna dostupná pole. To ovšem může mít za následek zbytečně velkou odezvu na dotaz, proto je potřeba pečlivě zvážit množinu skutečně potřebných polí pro vytvoření požadované odezvy. Naopak pole obsahující identifikátor záznamu je předáváno vždy. Záznamy v odezvě mohou být setříděny podle polí, která jsou k tomu v definici projektu určena, a to vzestupně nebo sestupně, v případě abecedně řazených polí také v požadovaném jazyce (pokud je podporován). Pole určená pro řazení záznamů nesmějí být vícenásobná (multivalued=true). Pokud má být seznam řazen podle relevance vyhledaných záznamů, identifikátor pole pro řazení bude score (viz dokumentace SOLR). Pokud je to požadováno (parametry requestu obsahují platný identifikátor pole pro facety "facet.field"), předá se také seznam facetů (ve formě rejstříku nebo našeptávače) pro požadované pole odpovídajících položenému dotazu. Je-li v parametrech requestu uveden „hl“ = true, bude také předán seznam záznamů se zvýrazněnými fragmenty odpovídajícími dotazu. Zvýrazňování se primárně provede podle nastavení v konfiguraci projektu, dále pak podle vložených parametrů.
   *
   * @param params
   */
  formSearch<T>(params: FormSearchParams): Observable<Search<T>> {
    return this.get<Search<T>>('search/formSearch', params);
  }

  formSolrSearch<T>(params: FormSolrSearchParams): Observable<Search<T>> {
    return this.get<Search<T>>('search/formSolrSearch', params);
  }

  /**
   * Request předá záznam s požadovaným identifikátorem záznamu v daném core na daném SOLR serveru. Vyhledání požadovaného záznamu se provede prostřednictvím SOLR dotazu ve tvaru: <fieldId_pole_s_identifikátorem_záznamu>:<identifikátor_záznamu>. Jako odezvu request předá vyhledaný záznam s požadovanou množinou polí. Pokud záznam s požadovaným identifikátorem nebyl nalezen, je předáno chybové hlášení "Záznam s identifikátorem <recordId> nebyl nalezen" (522). Pokud bylo nalezeno více záznamů k danému identifikátoru, jedná se o chybu dat a je generováno chybové hlášení "Neplatný identifikátor záznamu" (109). Je-li v parametrech requestu uveden „hl“ = true, bude také předán seznam zvýrazněných fragmentů odpovídajících dotazu uvedenému v parametru „hl.q“. V tomto parametru by měl být obsažen dotaz, který byl předán v odezvě „result.q“ jednoho z vyhledávacích requestů. Tím bude zajištěno to, že požadovaný záznam bude obarven v souladu s vyhledáváním. Pokud tento parametr nebude uveden, v záznamu bude obarveno pole s identifikátorem záznamu (pokud je požadováno v odezvě), protože záznam je předáván na základě SOLR dotazu na identifikátor záznamu. Zvýrazňování se primárně provede podle nastavení v konfiguraci projektu, dále pak podle vložených parametrů.
   *
   * @param params
   */
  getRecord<T extends RecordItem>(params: GetRecordParams): Observable<GetRecord<T>> {
    return this.get<GetRecord<T>>('search/getRecord', params);
  }

  /**
   * Request vytvoří našeptávač k požadovanému vyhledávacímu poli podle nastavených parametrů a v odezvě předá požadovaný počet výrazů (facetů) spolu s počtem jejich výskytů. Počet výskytů u každého výrazu odpovídá parametrům dotazu (SOLR query, prefix). Jednotlivé výrazy našeptávače jsou předávány jako lower-case (pouze s malými písmeny).
   *
   * @param params
   */
  autocomplete(params: AutocompleteParams): Observable<AutocompleteResult[]> {
    return this.get<AutocompleteResult[]>('search/autocomplete', params);
  }

  /**
   * Request sestaví rejstřík odpovídajících výrazů k požadovanému poli podle nastavených parametrů a v odezvě předá požadovaný počet výrazů (facetů) spolu s počtem jejich výskytů. Počet výskytů u každého výrazu odpovídá parametrům dotazu (SOLR query, prefix). Jednotlivé výrazy rejstříku jsou předávány v původním tvaru, s malými a velkými písmeny (case-sensitive).
   *
   * @param params
   */
  register(params: RegisterParams): Observable<AutocompleteResult[]> {
    return this.get<AutocompleteResult[]>('search/register', params);
  }

  detailReportInfo(): Observable<Report[]> {
    return this.get<Report[]>('info/detailReportInfo');
  }

  detailReportLang(reportLang: string): Observable<Report[]> {
    return this.get<Report[]>('info/detailReportLang', {
      reportLang
    }).pipe(
      catchError((err) => {
        if (err instanceof CastorError && (<CastorError>err).code === 124) {
          return of([]);
        }

        return throwError(err);
      })
    );
  }

  detailReportCore(coreId: string): Observable<Report[]> {
    return this.get<Report[]>('info/detailReportCore', {
      coreId
    }).pipe(
      catchError((err) => {
        if (err instanceof CastorError && (<CastorError>err).code === 124) {
          return of([]);
        }

        return throwError(err);
      })
    );
  }

  detailReportId(reportId: string): Observable<Report> {
    return this.get<Report>('info/detailReportId', {
      reportId: reportId
    });
  }

  getRecordReport(params: GetRecordReportParams): Observable<string> {
    return this.get<string>('search/getRecordReport', params);
  }

  getRegisterFields(params: RegisterFieldsParams): Observable<Field[]> {
    return this.get<Field[]>('info/registerFields', params);
  }

  heatmapInts2D(params: HeatmapParams): Observable<HeatmapInts2D> {
    return this.get<HeatmapInts2D>('search/heatmap', {
      ...params,
      heatmapFormat: 'ints2D'
    });
  }

  heatmapPng(params: HeatmapParams): Observable<HeatmapPng> {
    return this.get<HeatmapPng>('search/heatmap', {
      ...params,
      heatmapFormat: 'png'
    });
  }

  geoRegister(params: GeoRegisterParams): Observable<AutocompleteResult[]> {
    return this.get<AutocompleteResult[]>('search/geoRegister', params);
  }

  geoRegisterGroup(params: GeoRegisterGroupParams): Observable<GeoRegisterGroup> {
    return this.get<GeoRegisterGroup>('search/geoRegisterGroup', params);
  }

  geoRegisterGroupWithStat(params: GeoRegisterGroupParams): Observable<GeoRegisterGroupWithStat> {
    return this.get<GeoRegisterGroupWithStat>('search/geoRegisterGroup', {
      ...params,
      stat: true,
      'stat.prefixEnabled': true
    });
  }

  registerGroup(params: RegisterGroupParams): Observable<RegisterGroup> {
    return this.get<RegisterGroup>('search/registerGroup', params);
  }

  registerGroupWithStat(params: RegisterGroupParams): Observable<RegisterGroupWithStat> {
    return this.get<RegisterGroupWithStat>('search/registerGroup', {
      ...params,
      stat: true,
      'stat.prefixEnabled': true
    });
  }

  registerTree(params: RegisterTreeParams): Observable<RegisterTreeResponse> {
    return this.get<RegisterTreeResponse>('search/registerTree', params);
  }

  rangeFacet(params: RangeFacetParams): Observable<RangeFacetResponse> {
    return this.get<RangeFacetResponse>('search/rangeFacet', params);
  }

  public request<T>(request: CastorRequest, params: Record<string, string | any> = {}): Observable<ResponseObject<T>> {
    return this.rawGet<T>(request, params).pipe(
      map((data) => {
        if (data.response.error.code !== 0) {
          throw new CastorError({
            code: data.response.error.code,
            message: data.response.error.msg,
            params: data.response.params
          });
        }

        return cloneDeep(data);
      })
    );
  }

  private get<T>(request: CastorRequest, params: Record<string, string | any> = {}): Observable<T> {
    return this.rawGet<T>(request, params).pipe(
      map((data) => {
        if (data.response.error.code !== 0) {
          throw new CastorError({
            code: data.response.error.code,
            message: data.response.error.msg,
            params: data.response.params
          });
        }

        return cloneDeep(data.response.result);
      })
    );
  }

  private mapParams(request: CastorRequest, params: Record<string, string | any>) {
    switch (request) {
      case 'search/register':
        return REGISTER_MAPPER.map(<RegisterParams>params);
      case 'search/getRecordReport':
        return GET_RECORD_REPORT_MAPPER.map(<GetRecordReportParams>params);
      case 'search/autocomplete':
        return AUTOCOMPLETE_MAPPER.map(<AutocompleteParams>params);
      case 'search/getRecord':
        return GET_RECORD_MAPPER.map(<GetRecordParams>params);
      case 'search/formSearch':
        return FORM_SEARCH_MAPPER.map(<FormSearchParams>params);
      case 'search/formSolrSearch':
        return FORM_SOLR_SEARCH_MAPPER.map(<FormSolrSearchParams>params);
      case 'search/solrSearch':
        return SOLR_SEARCH_MAPPER.map(<SolrSearchParams>params);
      case 'search/quickSearch':
        return QUICK_SEARCH_MAPPER.map(<QuickSearchParams>params);
      case 'search/geoRegisterGroup':
        return GEO_REGISTER_GROUP_MAPPER.map(<GeoRegisterGroupParams>params);
      case 'search/registerGroup':
        return REGISTER_GROUP_MAPPER.map(<RegisterGroupParams>params);
      case 'search/geoRegister':
        return GEO_REGISTER_MAPPER.map(<GeoRegisterParams>params);
      case 'search/registerTree':
        return REGISTER_TREE_MAPPER.map(<RegisterTreeParams>params);
      case 'search/rangeFacet':
        return RANGE_FACET_MAPPER.map(<RangeFacetParams>params);
    }

    return params;
  }

  private rawGet<T>(request: CastorRequest, params: Record<string, string | any> = {}): Observable<ResponseObject<T>> {
    let httpParams = new HttpParams();

    params = this.mapParams(request, params);

    for (const key of Object.keys(params)) {
      if (isNil(params[key])) continue;

      if (isObject(params[key])) continue;

      httpParams = httpParams.append(key, params[key]);
    }

    /*if (this.auth.isLoggedIn()) {
      httpParams = httpParams.append('jwt', this.auth.token);
    }*/

    return this.http
      .get(`${this.baseUrl}/${request}`, {
        params: httpParams,
        responseType: 'text'
      })
      .pipe(
        map((data: string) => {
          return <ResponseObject<T>>JSON.parse(data.replace(/\r?\n|\r/g, ''));
        })
      );
  }
}
